import React, { useState } from 'react';
import moment from 'moment';
import { motion } from 'framer-motion';
import { GoalDetailDTO, GoalDTO } from '../../../api/useResultsQuery';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Lozenge } from '../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { formatCurrency, formatPercentage } from '../../../../CommonUtils/formatNumber';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { RemoveGoalModal } from '../RemoveGoalModal/RemoveGoalModal';
import { VisualPickerButtonContainer } from '../../../../CommonComponents/OPL/VisualPickerButton/VisualPickerButtonContainer';
import PulseCheckLogo from '../../../img/tile-logo.svg';

const formatValue = (value: any, valueType: GoalDetailDTO['valueType']) => {
  switch (valueType) {
    case 'Date':
      return moment.utc(value).format('MM/DD/YYYY');
    case 'Amount':
      return formatCurrency(value, { mantissa: 2 });
    case 'Percent':
      return formatPercentage(value, { mantissa: 2 });
    default:
      return value;
  }
};

const AnimatedBox = motion(Box);

interface Props {
  clientId: string;
  categoryId: string;
  isEditing?: boolean;
  goal: GoalDTO;
}

export const GoalCard = ({ clientId, categoryId, isEditing, goal }: Props) => {
  const [isRemoveGoalModalOpen, setIsRemoveGoalModalOpen] = useState(false);

  return (
    <>
      <Wrapper clientId={clientId} goal={goal}>
        <Box width="100%" display="flex" flexDirection="column" p="16" css="gap: 16px">
          <Box display="flex" alignItems="center" css="gap: 8px">
            <PulseCheckLogo
              width={24}
              css={`
                color: ${({ theme }) => theme.colors.neutral['500']};
              `}
            />

            <Text fontSize="h5" fontWeight={600}>
              {goal.title}
            </Text>

            <Lozenge
              variantColor={
                goal.statusLevel === 0 ? 'default' : goal.statusLevel === 1 ? 'warning' : 'primary'
              }
              ml="auto"
            >
              {goal.status}
            </Lozenge>
          </Box>

          <Text fontSize="h6" fontWeight={500}>
            {goal.subtitle}
          </Text>

          <Box display="flex" flexDirection="column" css="gap: 8px">
            {goal.details.map(detail => (
              <Box key={detail.title} display="flex" justifyContent="space-between">
                <Text>{detail.title}</Text>

                <Text fontWeight={500}>{formatValue(detail.value, detail.valueType)}</Text>
              </Box>
            ))}
          </Box>

          <AnimatedBox
            initial={{ marginTop: -16, opacity: 0 }}
            animate={{ marginTop: isEditing ? 0 : -16, opacity: isEditing ? 1 : 0 }}
          >
            {isEditing && (
              <>
                <Button
                  onClick={evt => {
                    evt.stopPropagation();
                    ADV.util.Router.go2('PulseCheckSetGoal', {
                      clientId,
                      categoryId,
                      goalId: goal.id,
                    });
                  }}
                  leftIcon="far fa-pencil"
                  css="width: 100%"
                  mb="16"
                >
                  Edit This Goal
                </Button>

                <Button
                  variant="secondary"
                  onClick={evt => {
                    evt.stopPropagation();
                    setIsRemoveGoalModalOpen(true);
                  }}
                  leftIcon="far fa-trash-alt"
                  css="width: 100%"
                >
                  Remove This Goal
                </Button>
              </>
            )}
          </AnimatedBox>
        </Box>
      </Wrapper>

      {isRemoveGoalModalOpen && (
        <RemoveGoalModal
          clientId={clientId}
          goalId={goal.id}
          onClose={() => setIsRemoveGoalModalOpen(false)}
        />
      )}
    </>
  );
};

interface WrapperProps {
  clientId: string;
  goal: GoalDTO;
  children: React.ReactNode;
}

const Wrapper = ({ clientId, goal, children }: WrapperProps) => {
  const { navigationData } = goal;

  if (!navigationData) {
    return <Card>{children}</Card>;
  }

  return (
    <VisualPickerButtonContainer
      css={`
        box-shadow: ${({ theme }) => theme.shadows[400]};
        border: none;
        padding: 0;
      `}
      onClick={() => {
        if (navigationData?.GoalId) {
          ADV.util.Router.go2('NewFinancialPlanWhatIfs', {
            clientId,
            goalId: navigationData.GoalId,
            origin: 'pulseCheck',
          });
        }
      }}
    >
      {children}
    </VisualPickerButtonContainer>
  );
};
